import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import { TextStyle } from '@tiptap/extension-text-style';
import BackgroundColor from '@tiptap/extension-text-style/background-color';
import Color from '@tiptap/extension-color';
import Link from '@tiptap/extension-link';
import { Node as TiptapNode, mergeAttributes } from '@tiptap/core';
import MentionAutocomplete, { buildCitationHTML } from './MentionAutocomplete';
import RichFormatPill from './RichFormatPill';

const CitationNode = TiptapNode.create({
  name: 'citation',
  group: 'inline',
  inline: true,
  atom: true,

  addAttributes() {
    return {
      label: { default: '' },
      kind: { default: 'doc' },
      class: {
        default: 'ev-message-card6 m-0 mx-1 ev-btn-xsmall ev-bold-reg',
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span.ev-message-card6',
        getAttrs: (node) => ({
          label: node.textContent || '',
          kind: 'doc',
          class: node.getAttribute('class') || 'ev-message-card6 m-0 mx-1 ev-btn-xsmall ev-bold-reg',
        }),
      },
      {
        tag: 'span.ev-message-card7',
        getAttrs: (node) => ({
          label: node.textContent || '',
          kind: 'ref',
          class: node.getAttribute('class') || 'ev-message-card7 m-0 mx-1 ev-btn-xsmall ev-bold-reg',
        }),
      },
    ];
  },

  renderHTML({ node }) {
    const cls = node.attrs.class || (node.attrs.kind === 'ref'
      ? 'ev-message-card7 m-0 mx-1 ev-btn-xsmall ev-bold-reg'
      : 'ev-message-card6 m-0 mx-1 ev-btn-xsmall ev-bold-reg');
    return ['span', mergeAttributes({ class: cls }), node.attrs.label || ''];
  },
});

const normaliseClassAttr = (html) =>
  String(html || '').replace(/\sclassName=/gi, ' class=');

const renderInlineMarkdown = (value) => {
  let html = normaliseClassAttr(value);
  html = html.replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/(^|[\s(])_([^_\n]+)_/g, '$1<em>$2</em>');
  return html;
};

const markdownToHtml = (markdown) => {
  const lines = String(markdown || '').replace(/\r\n?/g, '\n').split('\n');
  const out = [];

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];
    if (!line.trim()) {
      out.push('<p></p>');
      continue;
    }

    const heading = line.match(/^(#{1,6})\s+(.+)$/);
    if (heading) {
      const level = Math.min(heading[1].length, 6);
      out.push(`<h${level}>${renderInlineMarkdown(heading[2])}</h${level}>`);
      continue;
    }

    const unordered = line.match(/^\s*[-*+]\s+(.+)$/);
    if (unordered) {
      const items = [];
      while (i < lines.length) {
        const item = lines[i].match(/^\s*[-*+]\s+(.+)$/);
        if (!item) break;
        items.push(`<li><p>${renderInlineMarkdown(item[1])}</p></li>`);
        i += 1;
      }
      i -= 1;
      out.push(`<ul>${items.join('')}</ul>`);
      continue;
    }

    const ordered = line.match(/^\s*\d+\.\s+(.+)$/);
    if (ordered) {
      const items = [];
      while (i < lines.length) {
        const item = lines[i].match(/^\s*\d+\.\s+(.+)$/);
        if (!item) break;
        items.push(`<li><p>${renderInlineMarkdown(item[1])}</p></li>`);
        i += 1;
      }
      i -= 1;
      out.push(`<ol>${items.join('')}</ol>`);
      continue;
    }

    out.push(`<p>${renderInlineMarkdown(line)}</p>`);
  }

  return out.join('');
};

const textOf = (node) => (node.textContent || '').replace(/\u200B/g, '');

const normaliseHex = (value) => {
  if (!value) return null;
  const raw = String(value).trim();
  const shortHex = raw.match(/^#?([0-9a-f]{3})$/i);
  if (shortHex) {
    return shortHex[1].split('').map((c) => c + c).join('').toUpperCase();
  }
  const hex = raw.match(/^#?([0-9a-f]{6})$/i);
  if (hex) return hex[1].toUpperCase();
  const rgb = raw.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
  if (rgb) {
    return [rgb[1], rgb[2], rgb[3]]
      .map((n) => Math.max(0, Math.min(255, Number(n))).toString(16).padStart(2, '0'))
      .join('')
      .toUpperCase();
  }
  return null;
};

const styleDecls = (el) => {
  const color = normaliseHex(el.style?.color);
  const bg = normaliseHex(el.style?.backgroundColor);
  const decls = [];
  if (color) decls.push(`color:#${color};`);
  if (bg) decls.push(`background-color:#${bg};`);
  return decls.join('');
};

const serializeInlineNode = (node) => {
  if (node.nodeType === Node.TEXT_NODE) return textOf(node);
  if (node.nodeType !== Node.ELEMENT_NODE) return '';

  const el = node;
  const tag = el.tagName.toLowerCase();
  const children = Array.from(el.childNodes).map(serializeInlineNode).join('');

  if (tag === 'br') return '\n';
  if (tag === 'span' && /\bev-message-card[67]\b/.test(el.className || '')) {
    return buildCitationHTML({
      kind: /\bev-message-card7\b/.test(el.className || '') ? 'ref' : 'doc',
      label: textOf(el),
    });
  }
  if (tag === 'strong' || tag === 'b') return `**${children}**`;
  if (tag === 'em' || tag === 'i') return `_${children}_`;
  if (tag === 'u') return `<u>${children}</u>`;
  if (tag === 'sup') return `<sup>${children}</sup>`;
  if (tag === 'sub') return `<sub>${children}</sub>`;
  if (tag === 'a') {
    const href = el.getAttribute('href');
    return href ? `<a href="${href}">${children}</a>` : children;
  }
  if (tag === 'span' || tag === 'mark') {
    const decls = styleDecls(el);
    return decls ? `<span style="${decls}">${children}</span>` : children;
  }

  return children;
};

const serializeBlockNode = (node) => {
  if (node.nodeType === Node.TEXT_NODE) return textOf(node);
  if (node.nodeType !== Node.ELEMENT_NODE) return '';

  const el = node;
  const tag = el.tagName.toLowerCase();
  const inline = () => Array.from(el.childNodes).map(serializeInlineNode).join('');

  if (/^h[1-6]$/.test(tag)) return `${'#'.repeat(Number(tag[1]))} ${inline()}\n\n`;
  if (tag === 'ul' || tag === 'ol') {
    return Array.from(el.children)
      .filter((child) => child.tagName?.toLowerCase() === 'li')
      .map((li, idx) => {
        const marker = tag === 'ol' ? `${idx + 1}.` : '-';
        const parts = Array.from(li.childNodes).map(serializeInlineNode).join('').trim();
        return `${marker} ${parts}`;
      })
      .join('\n') + '\n\n';
  }
  if (tag === 'p' || tag === 'div') return `${inline()}\n\n`;
  if (tag === 'br') return '\n';

  return `${inline()}\n\n`;
};

const htmlToReportMarkdown = (html) => {
  const doc = new DOMParser().parseFromString(`<div>${html || ''}</div>`, 'text/html');
  return Array.from(doc.body.firstChild.childNodes)
    .map(serializeBlockNode)
    .join('')
    .replace(/\u200B/g, '')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

const BlockToolbarButton = ({ active = false, label, title, onClick, children }) => (
  <button
    type="button"
    className={'genbi-agent-response-toolbar-btn' + (active ? ' active' : '')}
    title={title}
    aria-label={label}
    onMouseDown={(e) => {
      e.preventDefault();
      onClick();
    }}
  >
    {children}
  </button>
);

const AgentResponseEditor = ({
  value,
  onChange,
  docs = [],
  refs = [],
  onNeedReferences,
  onCreateReference,
  refsLoading = false,
}) => {
  const editorDomRef = useRef(null);
  const latestValueRef = useRef(value || '');
  const [tick, setTick] = useState(0);
  const [pillRect, setPillRect] = useState(null);
  const [pillMarks, setPillMarks] = useState({});

  const extensions = useMemo(() => [
    StarterKit.configure({
      heading: { levels: [1, 2, 3, 4, 5, 6] },
    }),
    Underline,
    TextStyle,
    Color,
    BackgroundColor,
    Link.configure({ openOnClick: false }),
    CitationNode,
  ], []);

  const editor = useEditor({
    extensions,
    content: markdownToHtml(value),
    editorProps: {
      attributes: {
        class: 'genbi-agent-response-editor-surface',
        spellcheck: 'true',
      },
    },
    onUpdate: ({ editor: instance }) => {
      const next = htmlToReportMarkdown(instance.getHTML());
      latestValueRef.current = next;
      onChange(next);
      setTick((n) => n + 1);
    },
    onSelectionUpdate: () => setTick((n) => n + 1),
  });

  useEffect(() => {
    editorDomRef.current = editor?.view?.dom || null;
  }, [editor, tick]);

  const refreshPill = useCallback(() => {
    const root = editorDomRef.current;
    const sel = window.getSelection();
    if (!editor || !root || !sel || sel.rangeCount === 0 || sel.isCollapsed) {
      setPillRect(null);
      return;
    }
    if (!root.contains(sel.anchorNode) || !root.contains(sel.focusNode)) {
      setPillRect(null);
      return;
    }
    const range = sel.getRangeAt(0);
    const firstRect = Array.from(range.getClientRects()).find((r) => r.width || r.height);
    const rect = range.getBoundingClientRect();
    const nextRect = firstRect || (rect && (rect.width || rect.height) ? rect : null);
    if (!nextRect) {
      setPillRect(null);
      return;
    }
    setPillMarks({
      bold: editor.isActive('bold'),
      italic: editor.isActive('italic'),
      underline: editor.isActive('underline'),
      color: editor.getAttributes('textStyle')?.color || null,
      bg: editor.getAttributes('textStyle')?.backgroundColor || null,
    });
    setPillRect(nextRect);
  }, [editor]);

  useEffect(() => {
    if (!editor) return undefined;
    const raf = window.requestAnimationFrame(refreshPill);
    return () => window.cancelAnimationFrame(raf);
  }, [editor, refreshPill, tick]);

  useEffect(() => {
    if (!editor) return undefined;
    let raf = 0;
    const onScrollOrResize = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        refreshPill();
      });
    };
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [editor, refreshPill]);

  useEffect(() => {
    if (!editor) return;
    const next = value || '';
    if (next === latestValueRef.current) return;
    latestValueRef.current = next;
    editor.commands.setContent(markdownToHtml(next), false);
  }, [editor, value]);

  const insertCitation = useCallback((item, meta = {}) => {
    if (!editor || !item) return;
    const cls = item.kind === 'ref'
      ? 'ev-message-card7 m-0 mx-1 ev-btn-xsmall ev-bold-reg'
      : 'ev-message-card6 m-0 mx-1 ev-btn-xsmall ev-bold-reg';
    const queryLength = (meta.query || '').length + 1;
    const to = editor.state.selection.from;
    const from = Math.max(1, to - queryLength);

    editor
      .chain()
      .focus()
      .deleteRange({ from, to })
      .insertContent({ type: 'citation', attrs: { label: item.label, kind: item.kind, class: cls } })
      .run();
  }, [editor]);

  const runFloatingFormat = useCallback((key, value) => {
    if (!editor) return;
    const chain = editor.chain().focus();
    if (key === 'bold') chain.toggleBold().run();
    else if (key === 'italic') chain.toggleItalic().run();
    else if (key === 'underline') chain.toggleUnderline().run();
    else if (key === 'color') (value ? chain.setColor(value) : chain.unsetColor()).run();
    else if (key === 'bg') {
      (value
        ? chain.setMark('textStyle', { backgroundColor: value })
        : chain.setMark('textStyle', { backgroundColor: null }).removeEmptyTextStyle()
      ).run();
    }
    window.requestAnimationFrame(refreshPill);
  }, [editor, refreshPill]);

  if (!editor) return null;

  return (
    <div className="genbi-agent-response-editor">
      <div className="genbi-agent-response-toolbar" aria-label="Response block formatting">
        <BlockToolbarButton
          active={editor.isActive('heading', { level: 1 })}
          label="Heading 1"
          title="Heading 1"
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        >
          <i className="bi bi-type-h1"></i>
        </BlockToolbarButton>
        <BlockToolbarButton
          active={editor.isActive('heading', { level: 2 })}
          label="Heading 2"
          title="Heading 2"
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        >
          <i className="bi bi-type-h2"></i>
        </BlockToolbarButton>
        <BlockToolbarButton
          active={editor.isActive('heading', { level: 3 })}
          label="Heading 3"
          title="Heading 3"
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        >
          <i className="bi bi-type-h3"></i>
        </BlockToolbarButton>
        <BlockToolbarButton
          active={editor.isActive('bulletList')}
          label="Bullet list"
          title="Bullet list"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          <i className="bi bi-list-ul"></i>
        </BlockToolbarButton>
        <BlockToolbarButton
          active={editor.isActive('orderedList')}
          label="Numbered list"
          title="Numbered list"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          <i className="bi bi-list-ol"></i>
        </BlockToolbarButton>
      </div>
      <EditorContent editor={editor} />

      <RichFormatPill
        visible={!!pillRect}
        anchorRect={pillRect}
        clipEl={editorDomRef.current}
        mode="text"
        marks={pillMarks}
        onToggle={(key) => runFloatingFormat(key)}
        onColor={(color) => runFloatingFormat('color', color)}
        onHighlight={(color) => runFloatingFormat('bg', color)}
        onRemove={(key) => runFloatingFormat(key, null)}
      />

      <MentionAutocomplete
        containerRef={editorDomRef}
        contentEditable
        docs={docs}
        refs={refs}
        onNeedReferences={onNeedReferences}
        onCreateReference={onCreateReference}
        refsLoading={refsLoading}
        onSelectItem={insertCitation}
      />
    </div>
  );
};

export default AgentResponseEditor;
